import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, RefreshCw, X, Trash2, CheckCircle2, Ban, Wallet, FileText, Search, Pencil, Repeat, FileMinus, ShoppingCart } from 'lucide-react';
import api from '../../services/api';
import { downloadFile } from '../../components/crm/billing/ui';
import { DealSelect } from '../../components/crm/billing/CustomerFinancePanel';
import ItemPicker, { CostCentreSelect } from '../../components/crm/billing/ItemPicker';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import SearchableSelect from '../../components/crm/SearchableSelect';
import DebitNoteModal from '../../components/crm/billing/DebitNoteModal';
import RecurringModal from '../../components/crm/billing/RecurringModal';
import NotesList from '../../components/crm/billing/NotesList';
import PurchaseOrdersView from '../../components/crm/billing/PurchaseOrdersView';
import BankAccountFields from '../../components/crm/billing/BankAccountFields';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const STATUS_STYLES = {
  draft: 'bg-[#F3F2F2] text-[#444444]',
  approved: 'bg-[#E0F2FE] text-[#0176D3]',
  partial: 'bg-[#FEF3C7] text-[#92400E]',
  paid: 'bg-[#DCFCE7] text-[#15803D]',
  void: 'bg-[#F3F4F6] text-[#9CA3AF] line-through',
};
const STATUS_LABEL = { draft: 'Draft', approved: 'Unpaid', partial: 'Part settled', paid: 'Settled', void: 'Void' };
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft', status: 'draft' },
  { key: 'open', label: 'Unpaid', status: 'approved,partial' },
  { key: 'overdue', label: 'Overdue', overdue: true },
  { key: 'paid', label: 'Paid', status: 'paid' },
  { key: 'void', label: 'Void', status: 'void' },
];
const METHODS = [
  { id: 'bank_transfer', label: 'Bank transfer' }, { id: 'upi', label: 'UPI' }, { id: 'cash', label: 'Cash' },
  { id: 'cheque', label: 'Cheque' }, { id: 'card', label: 'Card' }, { id: 'other', label: 'Other' },
];
const INPUT = 'w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3]';
const LABEL = 'block text-[11px] font-semibold text-[#706E6B] mb-1';
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const errMsg = (e, fallback) => e?.response?.data?.message || e?.response?.data?.error || fallback;
const emptyLine = (accountId = '') => ({ description: '', hsnCode: '', quantity: 1, unitPrice: '', taxRate: 18, discountPercent: 0, itcEligible: true, expenseAccountId: accountId });

function StatusPill({ status }) {
  return <span className={cn('px-2 py-0.5 rounded-full text-[11px] font-semibold', STATUS_STYLES[status])}>{STATUS_LABEL[status] || status}</span>;
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-start justify-center overflow-y-auto sm:p-4" onMouseDown={onClose}>
      <div className={cn('bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full sm:my-8', wide ? 'max-w-5xl' : 'max-w-xl')} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-[#F3F2F2]"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Client-side preview only — the server recomputes every amount on save.
function previewTotals(lines, pricesIncludeTax, reverseCharge) {
  let net = 0; let tax = 0;
  lines.forEach((l) => {
    const gross = (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0) * (1 - (Number(l.discountPercent) || 0) / 100);
    const rate = Number(l.taxRate) || 0;
    const n = pricesIncludeTax && rate ? gross / (1 + rate / 100) : gross;
    net += n; tax += pricesIncludeTax && rate ? gross - n : n * rate / 100;
  });
  return { net, tax, total: reverseCharge ? net : net + tax };
}

function BillForm({ bill, vendors, accounts, onClose, onSaved }) {
  const defaultAccount = accounts.find((a) => a.code === '5000')?._id || accounts[0]?._id || '';
  const [form, setForm] = useState(() => (bill ? {
    vendorId: bill.vendorId?._id || bill.vendorId, vendorBillNumber: bill.vendorBillNumber || '', dealId: bill.dealId?._id || bill.dealId || null,
    billDate: bill.billDate?.slice(0, 10), dueDate: bill.dueDate?.slice(0, 10),
    pricesIncludeTax: bill.pricesIncludeTax, reverseCharge: bill.reverseCharge, taxLabel: bill.taxLabel || 'Tax', notes: bill.notes || '',
    noTax: bill.taxMode === 'none',
    lineItems: bill.lineItems.map((l) => ({ ...l, expenseAccountId: l.expenseAccountId?._id || l.expenseAccountId })),
  } : {
    vendorId: '', vendorBillNumber: '', billDate: today(), dueDate: '', pricesIncludeTax: false, reverseCharge: false,
    taxLabel: 'Tax', notes: '', noTax: false, lineItems: [emptyLine(defaultAccount)],
  }));
  const [saving, setSaving] = useState(false);
  const currency = useCompanyCurrency();
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setLine = (i, patch) => setForm((f) => ({ ...f, lineItems: f.lineItems.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));

  const vendor = vendors.find((v) => v._id === form.vendorId);
  const totals = previewTotals(form.lineItems, form.pricesIncludeTax, form.reverseCharge);

  const onVendor = (id) => {
    const v = vendors.find((x) => x._id === id);
    setForm((f) => ({
      ...f, vendorId: id,
      lineItems: f.lineItems.map((l) => ({ ...l, expenseAccountId: l.description ? l.expenseAccountId : (v?.defaultExpenseAccountId || l.expenseAccountId) })),
    }));
  };

  const save = async () => {
    if (!form.vendorId) return alertService.error('Choose a vendor');
    if (form.lineItems.some((l) => !l.description?.trim() || l.unitPrice === '')) return alertService.error('Every line needs a description and a price');
    setSaving(true);
    const payload = {
      vendorId: form.vendorId, vendorBillNumber: form.vendorBillNumber.trim(), billDate: form.billDate, dueDate: form.dueDate || undefined, dealId: form.dealId || null,
      pricesIncludeTax: form.pricesIncludeTax, reverseCharge: form.reverseCharge, taxLabel: form.taxLabel, notes: form.notes,
      taxMode: form.noTax ? 'none' : undefined,
      lineItems: form.lineItems.map((l) => ({
        description: l.description, hsnCode: l.hsnCode, quantity: Number(l.quantity), unitPrice: Number(l.unitPrice),
        discountPercent: Number(l.discountPercent) || 0, taxRate: Number(l.taxRate) || 0, itcEligible: l.itcEligible, expenseAccountId: l.expenseAccountId,
        itemId: l.itemId || null, costCentreId: l.costCentreId?._id || l.costCentreId || null,
      })),
    };
    try {
      if (bill) await api.put(`/crm/purchases/bills/${bill._id}`, payload);
      else await api.post('/crm/purchases/bills', payload);
      onSaved();
    } catch (e) {
      alertService.error('Could not save bill', errMsg(e, 'Please check the details and try again'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={bill ? `Edit ${bill.billNumber}` : 'New purchase bill'} onClose={onClose} wide>
      <div className="p-5 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className={LABEL}>Vendor</label>
            <SearchableSelect value={form.vendorId} onChange={onVendor} options={vendors.filter((v) => v.status !== 'inactive')} placeholder="Choose vendor…" />
            {vendor && <p className="text-[11px] text-[#706E6B] mt-1">{[vendor.gstin && `GSTIN ${vendor.gstin}`, vendor.state].filter(Boolean).join(' · ') || 'No GSTIN on file'}</p>}
          </div>
          <div>
            <label className={LABEL}>Vendor's invoice no.</label>
            <input aria-label="Vendor's invoice no." className={INPUT} value={form.vendorBillNumber} onChange={(e) => set({ vendorBillNumber: e.target.value })} placeholder="As printed on their bill" />
            <label className={cn(LABEL, 'mt-2')}>For deal / project (optional)</label>
            <DealSelect value={form.dealId} onChange={(dealId) => set({ dealId })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className={LABEL}>Bill date</label>
              <input aria-label="Bill date" type="date" className={INPUT} value={form.billDate} onChange={(e) => set({ billDate: e.target.value })} />
            </div>
            <div>
              <label className={LABEL}>Due date</label>
              <input aria-label="Due date" type="date" className={INPUT} value={form.dueDate} min={form.billDate} onChange={(e) => set({ dueDate: e.target.value })} title="Leave blank to use the vendor's payment terms" />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-[#2E2E2E]">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.pricesIncludeTax} onChange={(e) => set({ pricesIncludeTax: e.target.checked })} /> Prices include tax</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.noTax} onChange={(e) => set({ noTax: e.target.checked })} /> No tax on this bill</label>
          <label className="flex items-center gap-2" title="GST reverse charge: the vendor doesn't charge tax; you pay it to the government and claim it back"><input type="checkbox" checked={form.reverseCharge} onChange={(e) => set({ reverseCharge: e.target.checked })} /> Reverse charge (GST)</label>
        </div>

        <div className="border border-[#D8DDE6] rounded overflow-x-auto">
          <table className="w-full text-[12px] sm:min-w-[53.75rem]">
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left font-semibold px-2 py-2">Item / description</th>
                <th className="text-left font-semibold px-2 py-2 w-24">HSN/SAC</th>
                <th className="text-left font-semibold px-2 py-2 w-48">Expense account</th>
                <th className="text-right font-semibold px-2 py-2 w-16">Qty</th>
                <th className="text-right font-semibold px-2 py-2 w-24">Rate</th>
                <th className="text-right font-semibold px-2 py-2 w-16">Disc %</th>
                <th className="text-right font-semibold px-2 py-2 w-16">Tax %</th>
                <th className="text-center font-semibold px-2 py-2 w-16" title="Claim this line's tax as input tax credit">Claim</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {form.lineItems.map((l, i) => (
                <tr key={i} className="border-t border-[#F3F2F2]">
                  <td className="px-2 py-1.5">
                    <div className="flex gap-1">
                      <ItemPicker value={l.itemId} className="w-28" onPick={(it) => setLine(i, it
                        ? { itemId: it._id, description: it.description || it.name, hsnCode: it.hsnCode || l.hsnCode, unitPrice: it.purchasePrice || l.unitPrice, taxRate: it.taxRate ?? l.taxRate, ...(it.expenseAccountId ? { expenseAccountId: it.expenseAccountId } : {}) }
                        : { itemId: null })} />
                      <input className={INPUT} aria-label="Item description" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="What was bought" />
                    </div>
                    <CostCentreSelect value={l.costCentreId} onChange={(costCentreId) => setLine(i, { costCentreId })} className="mt-1 w-full h-8" />
                  </td>
                  <td className="px-2 py-1.5"><input className={INPUT} aria-label="HSN/SAC code" value={l.hsnCode} onChange={(e) => setLine(i, { hsnCode: e.target.value })} /></td>
                  <td className="px-2 py-1.5">
                    <select className={INPUT} aria-label="Expense account" value={l.expenseAccountId} onChange={(e) => setLine(i, { expenseAccountId: e.target.value })}>
                      {accounts.map((a) => <option key={a._id} value={a._id}>{a.code} · {a.name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5"><input type="number" min="0" step="0.01" className={cn(INPUT, 'text-right')} aria-label="Quantity" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></td>
                  <td className="px-2 py-1.5"><input type="number" min="0" step="0.01" className={cn(INPUT, 'text-right')} aria-label="Rate" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} /></td>
                  <td className="px-2 py-1.5"><input type="number" min="0" max="100" step="0.01" className={cn(INPUT, 'text-right')} aria-label="Discount percent" value={l.discountPercent} onChange={(e) => setLine(i, { discountPercent: e.target.value })} /></td>
                  <td className="px-2 py-1.5"><input type="number" min="0" max="100" step="0.01" disabled={form.noTax} className={cn(INPUT, 'text-right disabled:bg-[#F3F2F2]')} aria-label="Tax percent" value={l.taxRate} onChange={(e) => setLine(i, { taxRate: e.target.value })} /></td>
                  <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={l.itcEligible} disabled={form.noTax} onChange={(e) => setLine(i, { itcEligible: e.target.checked })} aria-label="Claim input tax credit" /></td>
                  <td className="px-1 text-center">
                    {form.lineItems.length > 1 && (
                      <button type="button" aria-label="Remove line" onClick={() => setForm((f) => ({ ...f, lineItems: f.lineItems.filter((_, idx) => idx !== i) }))} className="p-1 text-[#706E6B] hover:text-[#BA0517]"><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" onClick={() => setForm((f) => ({ ...f, lineItems: [...f.lineItems, emptyLine(vendor?.defaultExpenseAccountId || defaultAccount)] }))} className="-mt-3 text-[12px] font-semibold text-[#0176D3] flex items-center gap-1"><Plus size={14} /> Add line</button>

        <div className="flex flex-col md:flex-row gap-5 justify-between">
          <div className="flex-1">
            <label className={LABEL}>Notes</label>
            <textarea aria-label="Notes" rows={3} className={cn(INPUT, 'h-auto py-2')} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
          </div>
          <div className="w-full md:w-72 text-[13px] space-y-1.5">
            <div className="flex justify-between text-[#706E6B]"><span>Subtotal</span><span className="tabular-nums">{formatCurrency(totals.net, currency)}</span></div>
            <div className="flex justify-between text-[#706E6B]"><span>Tax{form.reverseCharge ? ' (self-assessed)' : ''}</span><span className="tabular-nums">{formatCurrency(totals.tax, currency)}</span></div>
            <div className="flex justify-between font-bold text-[15px] text-[#2E2E2E] border-t border-[#D8DDE6] pt-2"><span>Payable to vendor</span><span className="tabular-nums">{formatCurrency(totals.total, currency)}</span></div>
            <p className="text-[11px] text-[#706E6B]">GST split (CGST/SGST or IGST) is worked out from the vendor's state when you save.</p>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#D8DDE6] bg-[#FAFAF9] rounded-b-lg">
        <button type="button" onClick={onClose} className="h-9 px-4 rounded border border-[#C9C9C9] text-[13px] font-semibold bg-white">Cancel</button>
        <button type="button" disabled={saving} onClick={save} className="hover:bg-[#014486] h-9 px-4 rounded bg-[#0176D3] text-white text-[13px] font-semibold disabled:opacity-60">{saving ? 'Saving…' : 'Save as draft'}</button>
      </div>
    </Modal>
  );
}

function PaymentForm({ vendors, presetVendorId, presetBillId, onClose, onSaved }) {
  const [vendorId, setVendorId] = useState(presetVendorId || '');
  const [openBills, setOpenBills] = useState([]);
  const [alloc, setAlloc] = useState({});
  const [meta, setMeta] = useState({ date: today(), method: 'bank_transfer', reference: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const currency = useCompanyCurrency();
  const vendor = vendors.find((v) => v._id === vendorId);
  const [tds, setTds] = useState({ on: false, section: '', rate: '' });
  useEffect(() => {
    setTds(vendor?.tdsRate > 0 ? { on: true, section: vendor.tdsSection || '', rate: String(vendor.tdsRate) } : { on: false, section: '', rate: '' });
  }, [vendor?._id, vendor?.tdsRate, vendor?.tdsSection]);

  useEffect(() => {
    if (!vendorId) { setOpenBills([]); return; }
    api.get('/crm/purchases/bills', { params: { vendorId, status: 'approved,partial', limit: 200 } })
      .then((res) => {
        const bills = [...(res.data.bills || [])].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
        setOpenBills(bills);
        setAlloc(presetBillId ? { [presetBillId]: String(bills.find((b) => b._id === presetBillId)?.balanceDue ?? '') } : {});
      })
      .catch(() => setOpenBills([]));
  }, [vendorId, presetBillId]);

  const total = Object.values(alloc).reduce((s, v) => s + (Number(v) || 0), 0);
  // Same basis as the server: TDS on the pre-GST share of what's being paid.
  const tdsAmount = tds.on && Number(tds.rate) > 0
    ? Math.round(Object.entries(alloc).reduce((s, [id, v]) => {
      const b = openBills.find((x) => x._id === id);
      return s + (b && b.totalAmount > 0 ? (Number(v) || 0) * (b.subtotal / b.totalAmount) : 0);
    }, 0) * Number(tds.rate)) / 100
    : 0;

  // Spread an amount across the oldest-due bills first.
  const [spread, setSpread] = useState('');
  const applySpread = () => {
    let left = Number(spread) || 0;
    const next = {};
    openBills.forEach((b) => {
      const take = Math.min(left, b.balanceDue);
      if (take > 0) { next[b._id] = String(Math.round(take * 100) / 100); left -= take; }
    });
    setAlloc(next);
  };

  const save = async () => {
    const allocations = Object.entries(alloc).map(([billId, amount]) => ({ billId, amount: Number(amount) })).filter((a) => a.amount > 0);
    if (!allocations.length) return alertService.error('Enter an amount against at least one bill');
    setSaving(true);
    try {
      await api.post('/crm/purchases/payments', { vendorId, ...meta, allocations, ...(tds.on && Number(tds.rate) > 0 ? { tdsRate: Number(tds.rate), tdsSection: tds.section } : {}) });
      onSaved();
    } catch (e) {
      alertService.error('Payment not recorded', errMsg(e, 'Please try again'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Record vendor payment" onClose={onClose} wide>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className={LABEL}>Vendor</label>
            <SearchableSelect value={vendorId} onChange={setVendorId} options={vendors} placeholder="Choose vendor…" />
          </div>
          <div><label className={LABEL}>Payment date</label><input aria-label="Payment date" type="date" className={INPUT} value={meta.date} onChange={(e) => setMeta({ ...meta, date: e.target.value })} /></div>
          <div>
            <label className={LABEL}>Method</label>
            <select aria-label="Method" className={INPUT} value={meta.method} onChange={(e) => setMeta({ ...meta, method: e.target.value })}>{METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}</select>
          </div>
          <div className="md:col-span-2"><label className={LABEL}>Reference (UTR / cheque no.)</label><input aria-label="Reference (UTR / cheque no.)" className={INPUT} value={meta.reference} onChange={(e) => setMeta({ ...meta, reference: e.target.value })} /></div>
          <div className="md:col-span-2"><label className={LABEL}>Notes</label><input aria-label="Notes" className={INPUT} value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} /></div>
          <BankAccountFields meta={meta} setMeta={setMeta} idPrefix="bp" />
          <div className="md:col-span-4 flex flex-wrap items-end gap-3 border-t border-[#F3F2F2] pt-3">
            <label className="flex items-center gap-2 text-[13px] h-9"><input type="checkbox" checked={tds.on} onChange={(e) => setTds({ ...tds, on: e.target.checked })} /> Deduct TDS</label>
            {tds.on && (
              <>
                <div className="w-32"><label className={LABEL} htmlFor="tds-sec">Section</label><input id="tds-sec" className={INPUT} placeholder="194C" value={tds.section} onChange={(e) => setTds({ ...tds, section: e.target.value.toUpperCase() })} /></div>
                <div className="w-24"><label className={LABEL} htmlFor="tds-rate">Rate %</label><input id="tds-rate" type="number" min="0" max="100" step="0.01" className={INPUT} value={tds.rate} onChange={(e) => setTds({ ...tds, rate: e.target.value })} /></div>
                <p className="text-[12px] text-[#706E6B]">TDS on the amount before GST. The bills are settled in full; only the net goes to the vendor.</p>
              </>
            )}
          </div>
        </div>

        {vendorId && (
          openBills.length === 0 ? (
            <p className="text-[13px] text-[#706E6B] py-6 text-center">This vendor has no unpaid bills.</p>
          ) : (
            <>
              <div className="flex items-end gap-2">
                <div className="w-48"><label className={LABEL}>Pay an amount, oldest due first</label><input aria-label="Pay an amount, oldest due first" type="number" min="0" step="0.01" className={INPUT} value={spread} onChange={(e) => setSpread(e.target.value)} /></div>
                <button type="button" onClick={applySpread} className="h-9 px-3 rounded border border-[#C9C9C9] text-[12px] font-semibold">Apply</button>
                <button type="button" onClick={() => setAlloc(Object.fromEntries(openBills.map((b) => [b._id, String(b.balanceDue)])))} className="h-9 px-3 rounded border border-[#C9C9C9] text-[12px] font-semibold">Pay all in full</button>
              </div>
              <div className="border border-[#D8DDE6] rounded overflow-x-auto">
                <table className="w-full text-[12px] sm:min-w-[40rem]">
                  <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
                    <tr>
                      <th className="text-left font-semibold px-3 py-2">Bill</th><th className="text-left font-semibold px-3 py-2">Due</th>
                      <th className="text-right font-semibold px-3 py-2">Total</th><th className="text-right font-semibold px-3 py-2">Balance due</th>
                      <th className="text-right font-semibold px-3 py-2 w-40">Pay now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openBills.map((b) => (
                      <tr key={b._id} className="border-t border-[#F3F2F2]">
                        <td className="px-3 py-2"><span className="font-semibold">{b.billNumber}</span>{b.vendorBillNumber && <span className="text-[#706E6B]"> · {b.vendorBillNumber}</span>}</td>
                        <td className={cn('px-3 py-2', new Date(b.dueDate) < new Date() && 'text-[#BA0517] font-semibold')}>{fmtDate(b.dueDate)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(b.totalAmount, b.currency)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(b.balanceDue, b.currency)}</td>
                        <td className="px-3 py-1.5"><input type="number" min="0" max={b.balanceDue} step="0.01" className={cn(INPUT, 'text-right')} aria-label="Amount to pay now" value={alloc[b._id] || ''} onChange={(e) => setAlloc({ ...alloc, [b._id]: e.target.value })} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )
        )}
      </div>
      <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-[#D8DDE6] bg-[#FAFAF9] rounded-b-lg">
        <span className="text-[13px] text-[#2E2E2E]">Settles <strong className="tabular-nums">{formatCurrency(total, openBills[0]?.currency || currency)}</strong>{tdsAmount > 0 && <> · TDS <strong className="tabular-nums">{formatCurrency(tdsAmount, openBills[0]?.currency || currency)}</strong> · pay vendor <strong className="tabular-nums">{formatCurrency(total - tdsAmount, openBills[0]?.currency || currency)}</strong></>}</span>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="h-9 px-4 rounded border border-[#C9C9C9] text-[13px] font-semibold bg-white">Cancel</button>
          <button type="button" disabled={saving || total <= 0} onClick={save} className="h-9 px-4 rounded bg-[#0176D3] text-white text-[13px] font-semibold disabled:opacity-60">{saving ? 'Saving…' : 'Record payment'}</button>
        </div>
      </div>
    </Modal>
  );
}

function BillDetail({ billId, onClose, onChanged, onEdit, onPay, onDebit, onRepeat }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/crm/purchases/bills/${billId}`).then((res) => setData(res.data)).catch((e) => alertService.error('Could not load bill', errMsg(e, '')));
  }, [billId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okText) => {
    try { await fn(); alertService.success(okText); load(); onChanged(); } catch (e) { alertService.error('Action failed', errMsg(e, 'Please try again')); }
  };
  if (!data) return <Modal title="Bill" onClose={onClose}><div className="p-8 text-center text-[13px] text-[#706E6B]">Loading…</div></Modal>;
  const { bill, payments } = data;
  const c = bill.currency;
  const taxRows = bill.taxMode === 'gst_intra'
    ? [['CGST', bill.cgstAmount], ['SGST', bill.sgstAmount]]
    : bill.taxMode === 'gst_inter' ? [['IGST', bill.igstAmount]] : bill.taxAmount > 0 ? [[bill.taxLabel || 'Tax', bill.taxAmount]] : [];

  return (
    <Modal title={`${bill.billNumber}${bill.vendorBillNumber ? ` · vendor #${bill.vendorBillNumber}` : ''}`} onClose={onClose} wide>
      <div className="p-5 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[15px] font-bold text-[#2E2E2E]">{bill.vendorId?.name}</p>
            <p className="text-[12px] text-[#706E6B]">{[bill.vendorGstin && `GSTIN ${bill.vendorGstin}`, bill.vendorState].filter(Boolean).join(' · ')}</p>
            <p className="text-[12px] text-[#706E6B] mt-1">Billed {fmtDate(bill.billDate)} · Due {fmtDate(bill.dueDate)}</p>
          </div>
          <div className="text-right">
            <StatusPill status={bill.status} />
            <p className="text-[22px] font-bold text-[#2E2E2E] tabular-nums mt-1">{formatCurrency(bill.totalAmount, c)}</p>
            {bill.status !== 'void' && bill.status !== 'draft' && <p className="text-[12px] text-[#706E6B]">Balance due <strong className="tabular-nums text-[#2E2E2E]">{formatCurrency(bill.balanceDue, c)}</strong></p>}
          </div>
        </div>

        <div className="border border-[#D8DDE6] rounded overflow-x-auto">
          <table className="w-full text-[12px] sm:min-w-[40rem]">
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr>
              <th className="text-left font-semibold px-3 py-2">Item</th><th className="text-left font-semibold px-3 py-2">Account</th>
              <th className="text-right font-semibold px-3 py-2">Qty</th><th className="text-right font-semibold px-3 py-2">Rate</th>
              <th className="text-right font-semibold px-3 py-2">Tax</th><th className="text-right font-semibold px-3 py-2">Amount</th>
            </tr></thead>
            <tbody>
              {bill.lineItems.map((l) => (
                <tr key={l._id} className="border-t border-[#F3F2F2]">
                  <td className="px-3 py-2">{l.description}{l.hsnCode && <span className="text-[#706E6B]"> · {l.hsnCode}</span>}{!l.itcEligible && l.taxAmount > 0 && <span className="ml-2 text-[10px] font-semibold text-[#92400E] bg-[#FEF3C7] px-1.5 py-0.5 rounded">credit blocked</span>}</td>
                  <td className="px-3 py-2 text-[#706E6B]">{l.expenseAccountId?.name || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{l.quantity}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(l.unitPrice, c)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{l.taxRate}%</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatCurrency(l.netAmount, c)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col md:flex-row justify-between gap-6">
          <div className="flex-1 text-[12px] text-[#706E6B] space-y-1">
            {bill.reverseCharge && <p className="text-[#92400E]">Reverse charge: tax is self-assessed and not paid to the vendor.</p>}
            {bill.itcAmount > 0 && <p>Input tax credit claimed: <strong className="text-[#2E2E2E]">{formatCurrency(bill.itcAmount, c)}</strong></p>}
            {bill.blockedTaxAmount > 0 && <p>Tax added to cost (credit blocked): <strong className="text-[#2E2E2E]">{formatCurrency(bill.blockedTaxAmount, c)}</strong></p>}
            {bill.notes && <p className="pt-2 text-[#2E2E2E]">{bill.notes}</p>}
            {bill.voidReason && <p className="text-[#BA0517]">Voided: {bill.voidReason}</p>}
            {bill.isRecurring && <p className="text-[#0176D3]">Repeats {bill.recurrence} · next draft on {fmtDate(bill.nextBillDate)}</p>}
          </div>
          <div className="w-full md:w-72 text-[13px] space-y-1.5">
            <div className="flex justify-between text-[#706E6B]"><span>Subtotal</span><span className="tabular-nums">{formatCurrency(bill.subtotal, c)}</span></div>
            {taxRows.map(([k, v]) => <div key={k} className="flex justify-between text-[#706E6B]"><span>{k}</span><span className="tabular-nums">{formatCurrency(v, c)}</span></div>)}
            <div className="flex justify-between font-bold text-[15px] border-t border-[#D8DDE6] pt-2"><span>Payable</span><span className="tabular-nums">{formatCurrency(bill.totalAmount, c)}</span></div>
            {bill.amountPaid > 0 && <div className="flex justify-between text-[#15803D]"><span>Paid</span><span className="tabular-nums">-{formatCurrency(bill.amountPaid, c)}</span></div>}
            {bill.amountDebited > 0 && <div className="flex justify-between text-[#15803D]"><span>Debit notes</span><span className="tabular-nums">-{formatCurrency(bill.amountDebited, c)}</span></div>}
          </div>
        </div>

        {payments.length > 0 && (
          <div>
            <h3 className="text-[12px] font-bold text-[#706E6B] uppercase tracking-wide mb-2">Payments</h3>
            <div className="space-y-1">
              {payments.map((p) => (
                <div key={p._id} className={cn('flex justify-between text-[12px] border border-[#D8DDE6] rounded px-3 py-2', p.status === 'void' && 'opacity-50 line-through')}>
                  <span>{fmtDate(p.date)} · {METHODS.find((m) => m.id === p.method)?.label || p.method}{p.reference ? ` · ${p.reference}` : ''}{p.tdsAmount > 0 ? ` · TDS ${formatCurrency(p.tdsAmount, c)}${p.tdsSection ? ` (${p.tdsSection})` : ''}` : ''}</span>
                  <span className="tabular-nums font-semibold">{formatCurrency(p.appliedToThisBill, c)}{p.amount !== p.appliedToThisBill && <span className="font-normal text-[#706E6B]"> of {formatCurrency(p.amount, c)}</span>}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap justify-end gap-2 px-5 py-3 border-t border-[#D8DDE6] bg-[#FAFAF9] rounded-b-lg">
        {bill.status === 'draft' && (
          <>
            <button type="button" onClick={async () => { if (!(await alertService.confirm('Delete draft?', 'This draft bill will be removed.', 'danger', 'Delete'))) return; try { await api.delete(`/crm/purchases/bills/${bill._id}`); alertService.success('Draft deleted'); onChanged(); onClose(); } catch (e) { alertService.error('Could not delete', errMsg(e, '')); } }} className="h-9 px-4 rounded border border-[#C9C9C9] text-[13px] font-semibold bg-white text-[#BA0517] flex items-center gap-1.5"><Trash2 size={14} /> Delete</button>
            <button type="button" onClick={() => onEdit(bill)} className="h-9 px-4 rounded border border-[#C9C9C9] text-[13px] font-semibold bg-white flex items-center gap-1.5"><Pencil size={14} /> Edit</button>
            <button type="button" onClick={async () => { if (await alertService.confirm('Approve bill?', 'It will be posted to your books and become payable. Approved bills cannot be edited.', 'question', 'Approve')) act(() => api.post(`/crm/purchases/bills/${bill._id}/approve`), 'Bill approved'); }} className="h-9 px-4 rounded bg-[#0176D3] text-white text-[13px] font-semibold flex items-center gap-1.5"><CheckCircle2 size={14} /> Approve</button>
          </>
        )}
        {bill.status !== 'void' && (
          <button type="button" onClick={() => onRepeat(bill)} className="h-9 px-4 rounded border border-[#C9C9C9] text-[13px] font-semibold bg-white flex items-center gap-1.5"><Repeat size={14} /> {bill.isRecurring ? 'Repeating' : 'Repeat'}</button>
        )}
        {['approved', 'partial'].includes(bill.status) && (
          <button type="button" onClick={() => onDebit(bill)} className="h-9 px-4 rounded border border-[#C9C9C9] text-[13px] font-semibold bg-white flex items-center gap-1.5"><FileMinus size={14} /> Debit note</button>
        )}
        {bill.status === 'approved' && bill.amountPaid === 0 && !(bill.amountDebited > 0) && (
          <button type="button" onClick={async () => { if (await alertService.confirm('Void bill?', 'Its ledger entries will be reversed. This cannot be undone.', 'danger', 'Void')) act(() => api.post(`/crm/purchases/bills/${bill._id}/void`, { reason: 'Voided from bill screen' }), 'Bill voided'); }} className="h-9 px-4 rounded border border-[#C9C9C9] text-[13px] font-semibold bg-white text-[#BA0517] flex items-center gap-1.5"><Ban size={14} /> Void</button>
        )}
        {['approved', 'partial'].includes(bill.status) && (
          <button type="button" onClick={() => onPay(bill)} className="h-9 px-4 rounded bg-[#2E844A] text-white text-[13px] font-semibold flex items-center gap-1.5"><Wallet size={14} /> Record payment</button>
        )}
      </div>
    </Modal>
  );
}

export default function PurchaseBillsPage() {
  const currency = useCompanyCurrency();
  const [tab, setTab] = useState('bills');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null); // null | 'new' | bill
  const [detailId, setDetailId] = useState(null);
  const [pay, setPay] = useState(null); // null | { vendorId, billId }
  const [debitBill, setDebitBill] = useState(null);
  const [repeatBill, setRepeatBill] = useState(null);
  const [listKey, setListKey] = useState(0);

  const vendorOptions = useMemo(() => vendors.map((v) => ({ ...v, name: v.name })), [vendors]);

  const loadBills = useCallback(async () => {
    setLoading(true);
    const f = FILTERS.find((x) => x.key === filter);
    try {
      const [b, s] = await Promise.all([
        api.get('/crm/purchases/bills', { params: { status: f.status, overdue: f.overdue ? 'true' : undefined, search: search || undefined, limit: 200 } }),
        api.get('/crm/purchases/summary'),
      ]);
      setBills(b.data.bills || []);
      setSummary(s.data.summary);
    } catch (e) {
      alertService.error('Could not load bills', errMsg(e, ''));
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  const loadPayments = useCallback(() => {
    api.get('/crm/purchases/payments').then((res) => setPayments(res.data.payments || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/crm/purchases/vendors').then((res) => setVendors(res.data.vendors || [])).catch(() => {});
    api.get('/crm/purchases/expense-accounts').then((res) => setAccounts(res.data.accounts || [])).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(loadBills, 250); return () => clearTimeout(t); }, [loadBills]);
  useEffect(() => { if (tab === 'payments') loadPayments(); }, [tab, loadPayments]);

  const refreshAll = () => { loadBills(); loadPayments(); };

  const cards = [
    { label: 'Total payable', value: summary?.payable, tone: 'text-[#2E2E2E]' },
    { label: 'Overdue', value: summary?.overdue, tone: 'text-[#BA0517]' },
    { label: 'Due in 7 days', value: summary?.dueThisWeek, tone: 'text-[#92400E]' },
    { label: 'Input tax credit this month', value: summary?.itcThisMonth, tone: 'text-[#2E844A]' },
  ];

  return (
    <div className={PAGE}>
      <PageHeader title="Purchase Bills" subtitle="Record vendor bills, claim input tax credit and pay what you owe." actions={<>
          <button type="button" onClick={refreshAll} aria-label="Refresh" className="h-9 w-9 flex items-center justify-center rounded border border-[#D8DDE6] bg-white"><RefreshCw size={15} className={loading ? 'animate-spin' : ''} /></button>
          <button type="button" onClick={() => setPay({})} className="h-9 px-4 rounded border border-[#C9C9C9] bg-white text-[13px] font-semibold flex items-center gap-1.5"><Wallet size={15} /> Pay Vendor</button>
          <button type="button" onClick={() => setForm('new')} className="h-9 px-4 rounded bg-[#0176D3] text-white text-[13px] font-semibold flex items-center gap-1.5"><Plus size={15} /> New Bill</button>
      </>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
            <p className="text-[11px] font-semibold text-[#706E6B] uppercase tracking-wide">{c.label}</p>
            <p className={cn('text-[20px] font-bold tabular-nums mt-1', c.tone)}>{formatCurrency(c.value || 0, currency)}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-1 border-b border-[#D8DDE6] overflow-x-auto">
        {[['bills', 'Bills', FileText], ['orders', 'Purchase orders', ShoppingCart], ['payments', 'Payments made', Wallet], ['debit-notes', 'Debit notes', FileMinus]].map(([k, l, Icon]) => (
          <button type="button" key={k} onClick={() => setTab(k)} className={cn('shrink-0 whitespace-nowrap px-4 py-2 text-[13px] font-semibold flex items-center gap-1.5 border-b-2 -mb-px', tab === k ? 'border-[#0176D3] text-[#0176D3]' : 'border-transparent text-[#706E6B]')}><Icon size={14} /> {l}</button>
        ))}
      </div>

      {tab === 'orders' ? (
        <PurchaseOrdersView vendors={vendorOptions} accounts={accounts} refreshKey={listKey} onChanged={loadBills}
          onOpenBill={(id) => { setTab('bills'); setDetailId(id); loadBills(); }} />
      ) : tab === 'debit-notes' ? (
        <NotesList kind="debit" refreshKey={listKey} onChanged={loadBills} />
      ) : tab === 'bills' ? (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <div className="flex flex-wrap items-center gap-2 p-3 border-b border-[#D8DDE6]">
            {FILTERS.map((f) => (
              <button type="button" key={f.key} onClick={() => setFilter(f.key)} className={cn('h-8 px-3 rounded text-[12px] font-semibold border', filter === f.key ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white text-[#444444] border-[#D8DDE6]')}>{f.label}</button>
            ))}
            <div className="relative ml-auto w-full sm:w-64">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#706E6B]" />
              <input className={cn(INPUT, 'pl-8')} placeholder="Search bill number…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] sm:min-w-[53.75rem]">
              <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
                <tr className="border-b border-[#D8DDE6]">
                  <th className="text-left font-semibold px-4 py-2.5">Bill</th><th className="text-left font-semibold px-4 py-2.5">Vendor</th>
                  <th className="text-left font-semibold px-4 py-2.5">Bill date</th><th className="text-left font-semibold px-4 py-2.5">Due</th>
                  <th className="text-right font-semibold px-4 py-2.5">Total</th><th className="text-right font-semibold px-4 py-2.5">Balance</th>
                  <th className="text-left font-semibold px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {bills.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-[#706E6B]">{loading ? 'Loading…' : 'No bills here yet. Add a vendor bill with "New bill".'}</td></tr>
                )}
                {bills.map((b) => {
                  const overdue = ['approved', 'partial'].includes(b.status) && new Date(b.dueDate) < new Date();
                  return (
                    <tr key={b._id} onClick={() => setDetailId(b._id)} className="border-b border-[#F3F2F2] hover:bg-[#F7F9FB] cursor-pointer">
                      <td className="px-4 py-2.5"><span className="font-semibold text-[#0176D3]">{b.billNumber}</span>{b.vendorBillNumber && <span className="block text-[11px] text-[#706E6B]">#{b.vendorBillNumber}</span>}</td>
                      <td className="px-4 py-2.5">{b.vendorId?.name || '—'}</td>
                      <td className="px-4 py-2.5">{fmtDate(b.billDate)}</td>
                      <td className={cn('px-4 py-2.5', overdue && 'text-[#BA0517] font-semibold')}>{fmtDate(b.dueDate)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(b.totalAmount, b.currency)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{['draft', 'void'].includes(b.status) ? '—' : formatCurrency(b.balanceDue, b.currency)}</td>
                      <td className="px-4 py-2.5"><StatusPill status={b.status} />{overdue && <span className="ml-1.5 text-[11px] font-semibold text-[#BA0517]">Overdue</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
          <table className="w-full text-[13px] sm:min-w-[47.5rem]">
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
              <tr className="border-b border-[#D8DDE6]">
                <th className="text-left font-semibold px-4 py-2.5">Date</th><th className="text-left font-semibold px-4 py-2.5">Vendor</th>
                <th className="text-left font-semibold px-4 py-2.5">Method</th><th className="text-left font-semibold px-4 py-2.5">Bills settled</th>
                <th className="text-right font-semibold px-4 py-2.5">Amount</th><th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-[#706E6B]">No vendor payments recorded yet.</td></tr>}
              {payments.map((p) => (
                <tr key={p._id} className={cn('border-b border-[#F3F2F2]', p.status === 'void' && 'opacity-50')}>
                  <td className="px-4 py-2.5">{fmtDate(p.date)}</td>
                  <td className="px-4 py-2.5">{p.vendorId?.name || '—'}</td>
                  <td className="px-4 py-2.5">{METHODS.find((m) => m.id === p.method)?.label || p.method}{p.reference && <span className="block text-[11px] text-[#706E6B]">{p.reference}</span>}</td>
                  <td className="px-4 py-2.5 text-[12px]">{p.allocations.map((a) => a.billId?.billNumber).filter(Boolean).join(', ')}</td>
                  <td className={cn('px-4 py-2.5 text-right tabular-nums font-semibold', p.status === 'void' && 'line-through')}>{formatCurrency(p.amount, p.currency)}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button type="button" onClick={() => downloadFile(`/crm/purchases/payments/${p._id}/pdf`, `Payment-${String(p._id).slice(-8).toUpperCase()}.pdf`)} className="text-[12px] font-semibold text-[#0176D3] mr-3">PDF</button>
                    {p.status === 'posted' ? (
                      <button type="button" onClick={async () => {
                        if (!(await alertService.confirm('Void payment?', 'The bills it settled become unpaid again and the ledger entry is reversed.', 'danger', 'Void'))) return;
                        try { await api.post(`/crm/purchases/payments/${p._id}/void`); alertService.success('Payment voided'); refreshAll(); } catch (e) { alertService.error('Could not void', errMsg(e, '')); }
                      }} className="text-[12px] font-semibold text-[#BA0517]">Void</button>
                    ) : <span className="text-[11px] text-[#706E6B]">Void</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <BillForm
          bill={form === 'new' ? null : form}
          vendors={vendorOptions}
          accounts={accounts}
          onClose={() => setForm(null)}
          onSaved={() => { setForm(null); alertService.success('Bill saved as draft'); loadBills(); }}
        />
      )}
      {debitBill && (
        <DebitNoteModal bill={debitBill} onClose={() => setDebitBill(null)}
          onSaved={() => { setDebitBill(null); setListKey((k) => k + 1); loadBills(); setDetailId(null); }} />
      )}
      {repeatBill && (
        <RecurringModal bill={repeatBill} onClose={() => setRepeatBill(null)}
          onSaved={() => { setRepeatBill(null); setDetailId(null); loadBills(); }} />
      )}
      {detailId && !form && !pay && !debitBill && !repeatBill && (
        <BillDetail
          billId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={loadBills}
          onEdit={(bill) => setForm(bill)}
          onPay={(bill) => setPay({ vendorId: bill.vendorId?._id || bill.vendorId, billId: bill._id })}
          onDebit={(bill) => setDebitBill(bill)}
          onRepeat={(bill) => setRepeatBill(bill)}
        />
      )}
      {pay && (
        <PaymentForm
          vendors={vendorOptions}
          presetVendorId={pay.vendorId}
          presetBillId={pay.billId}
          onClose={() => setPay(null)}
          onSaved={() => { setPay(null); alertService.success('Payment recorded'); refreshAll(); }}
        />
      )}
    </div>
  );
}
